import {
	CloseOutlined,
	DollarOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { Form, Input, Table, Tooltip, Typography } from 'antd';
import Modal from 'antd/es/modal/Modal';
import { payPeriodApi } from 'Api/payPeriod';
import Buttons from 'components/Global/Buttons';
import { useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';

const { Text } = Typography;

type CurrencyItem = {
	companyCurrencyId: string;
	currency: string;
	symbol: string;
	currencyPosition: string;
};

type ExchangeRateItem = {
	companyCurrencyId: string;
	exchangeRate: number;
	isFinalize: boolean;
};

type Props = {
	currencyModalData: {
		payPeriodId: string;
		modalOpen: boolean;
		payPeriodName: string;
		existingExchangeRate: any;
		currencies: CurrencyItem[];
	};
	fetchAllPayPeriodDetails: any;
	handleClose: () => void;
};

const AddCurrencyRateModal = (props: Props) => {
	const { currencyModalData, handleClose, fetchAllPayPeriodDetails } = props;
	const {
		payPeriodId,
		modalOpen,
		payPeriodName,
		currencies,
		existingExchangeRate,
	} = currencyModalData;

	const [loading, setLoading] = useState(false);
	const [existingCurrencyExchangeRate, setExistingCurrencyExchangeRate] =
		useState<ExchangeRateItem[]>([]);
	const [form] = Form.useForm();
	// Force component to re-render
	const [_, setForceUpdate] = useState({});

	useEffect(() => {
		if (modalOpen) {
			const _currencies = currencies.map((item) => {
				const rate = existingExchangeRate.find(
					(rate: ExchangeRateItem) =>
						rate.companyCurrencyId === item.companyCurrencyId
				);

				// Create initial form values with rate_[id] format
				if (rate?.conversionRate) {
					form.setFieldValue(
						`rate_${item.companyCurrencyId}`,
						rate.conversionRate
					);
				}

				return {
					...item,
					exchangeRate: rate?.conversionRate,
					isFinalize: rate?.isFinalize,
				};
			});

			setExistingCurrencyExchangeRate(_currencies);
		}
	}, [currencies, existingExchangeRate, modalOpen, form]);

	const handleSubmit = async () => {
		setLoading(true);
		try {
			// Validate all fields first
			const values = await form.validateFields();

			// Extract all rate_ fields from the values
			const updatedRates = Object.keys(values)
				.filter((key) => key.startsWith('rate_'))
				.map((key) => {
					const companyCurrencyId = key.replace('rate_', '');
					return {
						companyCurrencyId,
						exchangeRate: parseFloat(values[key]),
					};
				});
			await payPeriodApi.createExchangeRates({
				payPeriodId,
				currencyExchangeRate: updatedRates,
			});

			// Close modal on success
			toastText('Exchange rates saved successfully', 'success');
			handleClose();
			fetchAllPayPeriodDetails();
		} catch (error: any) {
			if (!error.errorFields) {
				const message =
					error?.response?.data?.message ||
					'Something went wrong in creating currency';

				toastText(message, 'error');
			}
		} finally {
			setLoading(false);
		}
	};

	const buttons = [
		{
			text: `Cancel`,
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false, // This should be false for cancel button
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: loading,
			onclick: () => {
				handleClose();
			},
		},
		{
			text: `Save`,
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: false, // Changed to false to use our custom submit handler
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: false,
			onclick: handleSubmit,
		},
	];

	const columns = [
		{
			title: 'Currency',
			dataIndex: 'currency',
			className: 'bg-white',
			key: 'currency',
			render: (_: any, record: any) => (
				<div className={styles['currency-cell']}>
					<span className={styles['currency-symbol']}>
						{record.symbol}
					</span>
					<span className={styles['currency-name']}>
						{record.currency}
					</span>
				</div>
			),
		},
		{
			title: 'Exchange Rate',
			dataIndex: 'companyCurrencyId',
			key: 'exchangeRate',
			className: 'bg-white',
			render: (companyCurrencyId: string, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Form.Item
						name={`rate_${companyCurrencyId}`}
						rules={[
							{
								validator: (_, value) => {
									if (
										value === undefined ||
										value === null ||
										value === ''
									) {
										return Promise.reject(
											new Error('Please enter exchange rate')
										);
									}
									const numValue = parseFloat(value);
									if (isNaN(numValue) || numValue <= 0) {
										return Promise.reject(
											new Error('Please enter a valid positive number')
										);
									}
									return Promise.resolve();
								},
							},
						]}
						className={styles['rate-form-item']}
					>
						<Input
							prefix={
								<DollarOutlined
									className={styles['input-icon']}
								/>
							}
							type="number"
							step="0.0001"
							min="0.0001"
							placeholder="Enter rate"
							className={styles['rate-input']}
							onChange={() => {
								// Force re-render to update equivalence
								setForceUpdate({});
							}}
							disabled={record.isFinalize}
						/>
					</Form.Item>
					{record.isFinalize && (
						<Tooltip title="One Payroll is finalized or approved with this currency so you can't change the exchange rate.">
							<InfoCircleOutlined
								style={{
									marginLeft: '8px',
									color: 'red',
								}}
							/>
						</Tooltip>
					)}
				</div>
			),
		},
		{
			title: 'Equivalence',
			className: 'bg-white',
			dataIndex: 'companyCurrencyId',
			key: 'equivalence',
			render: (companyCurrencyId: string, record: any) => {
				const rate = form.getFieldValue(`rate_${companyCurrencyId}`);
				return (
					<div className={styles['equivalence-cell']}>
						<Text>
							1 {record.currency} ={' '}
							{rate ? Number(rate).toFixed(2) : '-'} Kwacha
						</Text>
					</div>
				);
			},
		},
	];

	return (
		<Modal
			open={modalOpen}
			width={800}
			footer={null}
			closable={false}
			onCancel={handleClose}
			rootClassName="addCurrencyRateModalRoot"
			destroyOnClose
		>
			<div className={`${styles.modal} modal`}>
				<div className={styles['modal-header']}>
					{payPeriodName} - Currency Exchange Rates
					<div className={styles['close-icon']} onClick={handleClose}>
						<CloseOutlined />
					</div>
				</div>

				<div className={styles['modal-content']}>
					<Text className={styles['description']}>
						Set exchange rates for each currency for this pay
						period.
					</Text>

					<Form
						form={form}
						layout="vertical"
						className={styles['exchange-form']}
						onValuesChange={() => {
							// Simple approach to trigger re-render when any value changes
							setForceUpdate({});
						}}
					>
						<div className="currency-table">
							<Table
								dataSource={existingCurrencyExchangeRate}
								columns={columns}
								rowKey="companyCurrencyId"
								pagination={false}
								scroll={{ y: 240 }}
							/>
						</div>
					</Form>
				</div>

				<div className={styles['modal-footer']}>
					<Buttons buttons={buttons} />
				</div>
			</div>
		</Modal>
	);
};

export default AddCurrencyRateModal;
